/**
 * Generic, domain-agnostic "omnifilter" engine.
 *
 * The user types space-separated tokens into a single bar; each token is tested
 * against a set of declared fields. A field declares how it matches:
 *   - `substring` — case-insensitive substring of the field value (e.g. a title), OR
 *   - `exact`     — the raw token equals the field value (case-sensitive), optionally
 *                   after running the token through a `transform` (e.g. extract a
 *                   YouTube ID from a pasted URL before comparing).
 *
 * A token "matches" an item if it matches ANY field. Whether the *item* is shown
 * then depends on the match mode (carried on the parsed query):
 *   - match-any (`matchAll: false`, the default) — at least ONE token must match.
 *   - match-all (`matchAll: true`)               — EVERY token must match some field.
 *
 * We never classify a token as "an ID" vs "a word" — every token is tested against
 * every field, so an `exact`/`transform` field only adds ways for a token to match;
 * it never suppresses a substring match.
 *
 * Domains (media, libraries, …) supply their own field config + a `getFields` mapper;
 * see `pages/Library/utils/omniFilter.ts` and `pages/Libraries/utils/librariesOmniFilter.ts`.
 */

export type OmniMatchMode = 'substring' | 'exact';

export interface OmniFieldDef<R extends string = string> {
  /** Stable key for this field, also used as the match reason. */
  reason: R;
  /** User-facing label, kept lowercase to read as "Matches name, library ID". */
  label: string;
  /** How a token is compared to this field's value. */
  mode: OmniMatchMode;
  /**
   * Optional token transformer for `exact` fields. When present, each query token is
   * run through it at parse time and the result is what the field is compared against
   * (e.g. extract a YouTube ID from a pasted URL). When absent, an `exact` field
   * matches a raw token verbatim (case-sensitive).
   */
  transform?: (token: string) => string | null;
}

/** A bag of field values for a single item. Missing/empty values never match. */
export type OmniFields<R extends string = string> = Partial<Record<R, string | null | undefined>>;

export interface ParsedOmniQuery {
  /** Raw tokens, case preserved — used for `exact` matches and display. */
  rawTokens: string[];
  /** Lowercased tokens — used for case-insensitive `substring` matches. */
  lowerTokens: string[];
  /** reason → per-token transformed value (aligned to `rawTokens`), for transform fields. */
  transformed: Record<string, (string | null)[]>;
  /** true = every token must match (AND); false = any token may match (OR). */
  matchAll: boolean;
}

/** Count the space-separated tokens in a query (shared by the count badge). */
export function countOmniTokens(query: string | null | undefined): number {
  if (!query) return 0;
  return query.trim().split(/\s+/).filter(Boolean).length;
}

/** Parse a raw query string into reusable token sets. Returns null when empty. */
export function parseOmniQuery<R extends string>(
  query: string | null | undefined,
  defs: readonly OmniFieldDef<R>[],
  matchAll = false,
): ParsedOmniQuery | null {
  if (!query) return null;
  const rawTokens = query.split(/\s+/).filter(Boolean);
  if (rawTokens.length === 0) return null;

  const lowerTokens = rawTokens.map((token) => token.toLowerCase());

  const transformed: Record<string, (string | null)[]> = {};
  for (const def of defs) {
    if (def.mode !== 'exact' || !def.transform) continue;
    const transform = def.transform;
    transformed[def.reason] = rawTokens.map((token) => transform(token));
  }

  return { rawTokens, lowerTokens, transformed, matchAll };
}

/** True if token `i` matches ANY of the declared fields. */
function tokenMatchesAnyField<R extends string>(
  i: number,
  fields: OmniFields<R>,
  parsed: ParsedOmniQuery,
  defs: readonly OmniFieldDef<R>[],
): boolean {
  for (const def of defs) {
    const value = fields[def.reason];
    if (!value) continue;
    if (def.mode === 'substring') {
      if (value.toLowerCase().includes(parsed.lowerTokens[i])) return true;
    } else if (def.transform) {
      if (parsed.transformed[def.reason]?.[i] === value) return true;
    } else if (parsed.rawTokens[i] === value) {
      return true;
    }
  }
  return false;
}

/**
 * True if the given fields satisfy the parsed query. Per the parsed `matchAll` flag,
 * either EVERY token must match some field (AND) or at least ONE must (OR, the default).
 */
export function matchesOmniFilter<R extends string>(
  fields: OmniFields<R>,
  parsed: ParsedOmniQuery,
  defs: readonly OmniFieldDef<R>[],
): boolean {
  const tokenMatches = (i: number) => tokenMatchesAnyField(i, fields, parsed, defs);
  return parsed.matchAll
    ? parsed.rawTokens.every((_, i) => tokenMatches(i))
    : parsed.rawTokens.some((_, i) => tokenMatches(i));
}

export interface OmniMatch<R extends string = string> {
  reason: R;
  /** User-facing label, e.g. "YouTube ID". */
  label: string;
  /** All raw bar tokens that produced this match (first is shown; the rest go in a tooltip). */
  tokens: string[];
}

/**
 * Like {@link matchesOmniFilter}, but returns *which* fields matched and the raw
 * token(s) that produced each match (for display — the "Matches …" caption + tooltip).
 * For the hot filtering path prefer the boolean `matchesOmniFilter` (it early-returns).
 */
export function getOmniMatches<R extends string>(
  fields: OmniFields<R>,
  parsed: ParsedOmniQuery,
  defs: readonly OmniFieldDef<R>[],
): OmniMatch<R>[] {
  const matches: OmniMatch<R>[] = [];
  for (const def of defs) {
    const value = fields[def.reason];
    if (!value) continue;

    let tokens: string[];
    if (def.mode === 'substring') {
      const haystack = value.toLowerCase();
      tokens = parsed.rawTokens.filter((token) => haystack.includes(token.toLowerCase()));
    } else if (def.transform) {
      const transform = def.transform;
      tokens = parsed.rawTokens.filter((token) => transform(token) === value);
    } else {
      tokens = parsed.rawTokens.filter((token) => token === value);
    }

    if (tokens.length) matches.push({ reason: def.reason, label: def.label, tokens });
  }
  return matches;
}
